import { fetchEventSource } from '@microsoft/fetch-event-source';
import axios from 'axios';
import MercureAuthorizationPostResponse from '../../dto/response/MercureAuthorizationPostResponse';
import Notification from '../../dto/response/Notification';

declare let window: {
    emitter: any;
};

class RejectedByHub extends Error {}

/**
 * Chat messages and notifications are private updates. The hub delivers them only for the topics listed in the
 * short-lived subscriber token. It is sent as a Bearer header (EventSource cannot send headers, hence fetch), so each
 * tab's connection carries its own token. A new token is fetched before it expires and on every change of the shown
 * chat channels, and the connection is reopened with it.
 */
export default class Mercure {
    private static instance: Mercure | null = null;
    // reference-counted, because several mounted chats can show the same channel
    private static readonly channels = new Map<number, number>();
    // generous, because timers in background tabs can be throttled
    private static readonly refreshMarginInSeconds = 120;
    private static readonly minRetryIntervalInSeconds = 5;
    private static readonly maxRetryIntervalInSeconds = 60;
    // without a token, the client can only subscribe to the public topics (see src/Application/Mercure/Topics.php)
    private static readonly publicTopics = [
        `${import.meta.env.VITE_API_URL}hot-matches`,
        `${import.meta.env.VITE_API_URL}staff-market`,
    ];

    private connection: AbortController | null = null;
    private authorizationRequest: AbortController | null = null;
    // recorded only on authorized connections: public-only event ids would make the replay skip private updates
    private lastEventId: string | null = null;
    private authorizedTopics: string[] = [];
    // after an outage (e.g. a hub restart that may have lost its history), the replay may be incomplete
    private connectionFailed = false;
    private scheduledConnect: ReturnType<typeof setTimeout> | null = null;
    private connectionAttempt = 0;
    private connectRequested = false;
    private retryIntervalInSeconds = Mercure.minRetryIntervalInSeconds;

    constructor() {
        Mercure.instance?.close();
        Mercure.instance = this;
        this.connect();
    }

    public static watchChannel(channelId: number | null | undefined): void {
        if (typeof channelId !== 'number' || !Number.isInteger(channelId)) {
            return;
        }
        const watchers = Mercure.channels.get(channelId) ?? 0;
        Mercure.channels.set(channelId, watchers + 1);
        if (watchers === 0) {
            Mercure.instance?.requestConnect();
        }
    }

    public static unwatchChannel(channelId: number | null | undefined): void {
        if (typeof channelId !== 'number' || !Number.isInteger(channelId)) {
            return;
        }
        const watchers = Mercure.channels.get(channelId) ?? 0;
        if (watchers > 1) {
            Mercure.channels.set(channelId, watchers - 1);
            return;
        }
        if (Mercure.channels.delete(channelId)) {
            Mercure.instance?.requestConnect();
        }
    }

    // reconnects once for all channel changes of the same tick (e.g. unwatch + watch on a channel switch)
    private requestConnect(): void {
        if (this.connectRequested) {
            return;
        }
        this.connectRequested = true;
        queueMicrotask(() => {
            this.connectRequested = false;
            this.connect();
        });
    }

    private async connect(): Promise<void> {
        this.cancelScheduledConnect();

        const attempt = ++this.connectionAttempt;
        const channels = [
            ...Mercure.channels.keys(),
        ];
        // a superseded token request is cancelled; its result would be discarded anyway
        this.authorizationRequest?.abort();
        const authorizationRequest = new AbortController();
        this.authorizationRequest = authorizationRequest;
        let authorization: MercureAuthorizationPostResponse | null = null;
        let loggedOut = false;
        try {
            const response = await axios.post('/api/mercure/authorization', { channelIds: channels }, { signal: authorizationRequest.signal });
            authorization = MercureAuthorizationPostResponse.fromData(response.data);
        }
        catch (error) {
            // meanwhile keep the current connection, or fall back to the public topics
            loggedOut = axios.isAxiosError(error) && error.response?.status === 401;
        }

        // a newer attempt (e.g. after a channel change) or a newer instance supersedes this one
        if (attempt !== this.connectionAttempt || Mercure.instance !== this) {
            return;
        }

        if (authorization === null) {
            if (this.connection === null) {
                this.open(null, Mercure.publicTopics);
            }
            // a logged-out user gets no token: stay on the public topics
            if (!loggedOut) {
                this.scheduleConnect(this.nextRetryIntervalInSeconds());
            }
            return;
        }

        this.open(authorization.getToken(), authorization.getTopics());
        this.scheduleConnect(authorization.getExpiresInSeconds() - Mercure.refreshMarginInSeconds);
    }

    private open(token: string | null, topics: string[]): void {
        const url = new URL(import.meta.env.VITE_MERCURE_PUBLIC_URL);
        topics.forEach(topic => url.searchParams.append('topic', topic));

        const headers: Record<string, string> = {};
        if (token !== null) {
            headers.Authorization = `Bearer ${token}`;
        }
        // the hub replays what was published while the previous connection was being replaced
        if (this.lastEventId !== null) {
            headers['last-event-id'] = this.lastEventId;
        }

        this.connection?.abort();
        const connection = new AbortController();
        this.connection = connection;
        // the replay starts after the last event of the previous connection, so it skips newly subscribed channels
        const addsTopics = topics.some(topic => !this.authorizedTopics.includes(topic));
        let firstOpen = true;

        fetchEventSource(url.toString(), {
            headers,
            signal: connection.signal,
            openWhenHidden: true,
            onopen: async (response) => {
                if (response.status === 401 || response.status === 403) {
                    throw new RejectedByHub();
                }
                if (!response.ok || !response.headers.get('content-type')?.startsWith('text/event-stream')) {
                    // e.g. 503 while the hub restarts, or a proxy error page: retry with the same token
                    throw new Error(`Mercure hub responded with ${response.status}`);
                }
                // a public-only fallback connection carries no chat messages and keeps backing off the token retries
                if (token !== null) {
                    this.retryIntervalInSeconds = Mercure.minRetryIntervalInSeconds;
                    // chats reload what the replay cannot cover: everything without an event id (also on the
                    // library's internal reconnects), after an outage, and newly subscribed channels
                    if (this.lastEventId === null || this.connectionFailed || (firstOpen && addsTopics)) {
                        window.emitter.emit('chat-messages-stale');
                    }
                    this.connectionFailed = false;
                    this.authorizedTopics = topics;
                }
                firstOpen = false;
            },
            onmessage: (message) => {
                if (message.id && token !== null) {
                    this.lastEventId = message.id;
                }
                if (message.data) {
                    this.dispatch(message.data);
                }
            },
            onclose: () => {
                // the hub ends the stream e.g. on restart or when the token expires: reconnect
                throw new Error('Mercure stream closed');
            },
            onerror: (error) => {
                this.connectionFailed = true;
                if (error instanceof RejectedByHub) {
                    throw error;
                }

                return this.nextRetryIntervalInSeconds() * 1000;
            },
        }).catch(() => {
            // the hub rejected the token (e.g. expired, or a key mismatch): keep the public topics and fetch a new one
            if (this.connection === connection) {
                this.connection = null;
                if (token !== null) {
                    this.open(null, Mercure.publicTopics);
                }
                this.scheduleConnect(this.nextRetryIntervalInSeconds());
            }
        });
    }

    private dispatch(rawData: string): void {
        // a failing listener must not be mistaken for a broken connection
        try {
            const data = JSON.parse(rawData);
            switch (data.type) {
                case 'notification':
                    window.emitter.emit('notification', Notification.info(data.data.message, data.data.params));
                    break;
                case 'chat-message':
                    window.emitter.emit('chat-message', data.data);
                    break;
                case 'hot-matches':
                    window.emitter.emit('hot-matches', data.data);
                    break;
                case 'staff-market':
                    window.emitter.emit('staff-market-updated', data.data);
                    break;
            }
        }
        catch (error) {
            console.error('Could not handle Mercure update', error);
        }
    }

    private nextRetryIntervalInSeconds(): number {
        const interval = this.retryIntervalInSeconds;
        this.retryIntervalInSeconds = Math.min(interval * 2, Mercure.maxRetryIntervalInSeconds);

        return interval;
    }

    private scheduleConnect(inSeconds: number): void {
        this.cancelScheduledConnect();
        this.scheduledConnect = setTimeout(() => this.connect(), inSeconds * 1000);
    }

    private cancelScheduledConnect(): void {
        if (this.scheduledConnect !== null) {
            clearTimeout(this.scheduledConnect);
            this.scheduledConnect = null;
        }
    }

    private close(): void {
        this.cancelScheduledConnect();
        this.authorizationRequest?.abort();
        this.connection?.abort();
        this.connection = null;
        if (Mercure.instance === this) {
            Mercure.instance = null;
        }
    }
}
